export class CanvasDrawer {
  private ctx: CanvasRenderingContext2D
  // 绘制和编辑图形的示例代码
  private isDrawing = false
  private shape = { points: [] as any }
  constructor(private canvas: HTMLCanvasElement) {
    const context = canvas.getContext('2d')
    if (!context) {
      throw new Error('无法获取 Canvas 2D 上下文')
    }
    this.ctx = context

    this.addListener(canvas)
  }

  private addListener(canvas: HTMLCanvasElement) {
    canvas.addEventListener('mousemove', (e) => {
      if (!this.isDrawing) return

      const rect = canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top

      // 更新图形的大小
      this.shape?.points?.push([x, y])

      // 清除画布并重新绘制图形
      this.ctx.clearRect(0, 0, canvas.width, canvas.height)
      drawShape(this.shape)
    })

    canvas.addEventListener('mouseup', () => {
      this.isDrawing = false
      // 完成绘制，可以在这里添加保存图形的逻辑
    })

    const drawShape = (shape: {
      points: any
      type?: any
      x?: any
      y?: any
    }) => {
      this.ctx.beginPath()
      if (shape.type === 'rectangle') {
        this.ctx.rect(
          shape.x,
          shape.y,
          shape.points[1][0] - shape.x,
          shape.points[1][1] - shape.y
        )
      }
      this.ctx.stroke()
    }
  }

  // 绘制线条
  public drawLine(x1: number, y1: number, x2: number, y2: number): void {
    this.ctx.beginPath()
    this.ctx.moveTo(x1, y1)
    this.ctx.lineTo(x2, y2)
    this.ctx.stroke()
  }

  // 绘制矩形
  public drawRect(
    x: number,
    y: number,
    width: number,
    height: number,
    color = 'black'
  ): void {
    this.ctx.beginPath()
    this.ctx.rect(x, y, width, height)
    this.ctx.fillStyle = color
    this.ctx.fill()
    this.ctx.stroke()
  }

  // 绘制圆形
  public drawCircle(
    x: number,
    y: number,
    radius: number,
    color = 'black'
  ): void {
    this.ctx.beginPath()
    this.ctx.arc(x, y, radius, 0, 2 * Math.PI)
    this.ctx.fillStyle = color
    this.ctx.fill()
    this.ctx.stroke() // 绘制边
  }
  // 绘制椭圆
  public drawEllipse(
    x: number,
    y: number,
    radius: number,
    color = 'black'
  ): void {
    this.ctx.beginPath()
    this.ctx.ellipse(100, 100, 50, 30, 0, Math.PI * 2, 0, true) // x, y, radiusX, radiusY, rotation, startAngle, endAngle, anticlockwise
    this.ctx.fillStyle = color
    this.ctx.fill() // 填充椭圆
    this.ctx.stroke() // 绘制边
  }

  // 清除画布
  public clearCanvas(): void {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
  }

  // 设置颜色和线条宽度等属性
  public setProperties(color: string, lineWidth: number): void {
    this.ctx.strokeStyle = color
    this.ctx.lineWidth = lineWidth
  }

  // 在画布上绘制文本
  public drawText(
    text: string,
    x: number,
    y: number,
    color = 'black',
    fontSize = 16
  ): void {
    this.ctx.font = `${fontSize}px sans-serif`
    this.ctx.fillStyle = color
    this.ctx.fillText(text, x, y)
  }

  // 绘制图片
  public drawImage(
    image: CanvasImageSource,
    x: number,
    y: number,
    width = 50,
    height = 50
  ): void {
    this.ctx.drawImage(image, x, y, width, height)
  }

  // 绘制点
  public drawPoint(x: number, y: number, size: number, color = 'black'): void {
    this.ctx.beginPath()
    this.ctx.arc(x, y, size, 0, 2 * Math.PI)
    this.ctx.fillStyle = color
    this.ctx.fill()
    this.ctx.stroke()
  }

  // 绘制扇形
  public drawSector(
    x: number,
    y: number,
    radius: number,
    startAngle: number,
    endAngle: number,
    color = 'black'
  ): void {
    this.ctx.beginPath()
    this.ctx.moveTo(x, y)
    // 将角度转换为弧度
    startAngle = (startAngle * Math.PI) / 180
    endAngle = (endAngle * Math.PI) / 180
    this.ctx.arc(x, y, radius, startAngle, endAngle)
    this.ctx.closePath()
    this.ctx.fillStyle = color
    this.ctx.fill()
    this.ctx.stroke()
  }

  // 绘制圆角矩形
  public drawRoundedRect(
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
    color = 'black'
  ): void {
    // 确保圆角半径不超过矩形半边长的一半
    const maxRadius = Math.min(width, height) / 2
    radius = Math.min(radius, maxRadius)

    this.ctx.beginPath()
    this.ctx.moveTo(x + radius, y)
    this.ctx.arcTo(x + width, y, x + width, y + height, radius)
    this.ctx.arcTo(x + width, y + height, x, y + height, radius)
    this.ctx.arcTo(x, y + height, x, y, radius)
    this.ctx.arcTo(x, y, x + width, y, radius)
    this.ctx.closePath()
    this.ctx.fillStyle = color
    this.ctx.fill()
    this.ctx.stroke()
  }

  // 绘制三角形
  public drawTriangle(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    x3: number,
    y3: number,
    color = 'black'
  ): void {
    this.ctx.beginPath()
    this.ctx.moveTo(x1, y1)
    this.ctx.lineTo(x2, y2)
    this.ctx.lineTo(x3, y3)
    this.ctx.closePath()
    this.ctx.fillStyle = color
    this.ctx.fill()
    this.ctx.stroke()
  }

  // 绘制多边形
  public drawPolygon(
    points: Array<{ x: number; y: number }>,
    color = 'black'
  ): void {
    this.ctx.beginPath()
    this.ctx.moveTo(points[0].x, points[0].y)
    for (let i = 1; i < points.length; i++) {
      this.ctx.lineTo(points[i].x, points[i].y)
    }
    this.ctx.closePath()
    this.ctx.fillStyle = color
    this.ctx.fill()
    this.ctx.stroke()
  }

  // 绘制指示标牌
  public drawSign(
    x: number,
    y: number,
    text: string,
    textColor: string,
    bgColor: string,
    size: number,
    icon?: string
  ): void {
    const ctx = this.ctx
    if (!ctx) return

    // 设置文本样式
    ctx.font = `${size}px sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    // 绘制背景矩形
    ctx.fillStyle = bgColor
    ctx.fillRect(x - size * 2, y - size, size * 4, size * 2)

    // 绘制文本
    ctx.fillStyle = textColor
    ctx.fillText(text, x, y)

    // 如果提供了图标路径，则绘制图标
    if (icon) {
      const image = new Image()
      image.src = icon
      image.onload = () => {
        ctx.drawImage(image, x - size, y - size, size * 2, size * 2)
      }
    }
  }

  // 绘制带有自动判定方向的指向线的指示标牌，宽高自适应文本内容，并添加内边距
  public drawSignWithArrow(
    x: number,
    y: number,
    text: string,
    textColor: string,
    bgColor: string,
    fontSize: number,
    icon?: string,
    arrowDirection: 'up' | 'down' | 'left' | 'right' = 'up'
  ): void {
    const ctx = this.ctx
    if (!ctx) return

    // 测量文本宽度
    ctx.font = `${fontSize}px sans-serif`
    const textWidth = ctx.measureText(text).width

    // 设置内边距
    const padding = 5 // 例如，内边距为字体大小的一半

    // 根据文本宽度和内边距计算标牌的宽高
    const signWidth = textWidth + padding * 2
    const signHeight = fontSize + padding * 2

    // 根据标牌位置和尺寸自动判定指向线方向
    // const isVertical = Math.abs(x - this.canvas.width / 2) < Math.abs(y - this.canvas.height / 2)
    arrowDirection = this.autoDetectArrowDirection(x, y, fontSize)

    // 绘制背景矩形（标牌）
    ctx.fillStyle = bgColor
    ctx.fillRect(x - signWidth / 2, y - signHeight / 2, signWidth, signHeight)

    // 绘制文本
    ctx.fillStyle = textColor
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(text, x, y)

    // 如果提供了图标路径，则绘制图标
    if (icon) {
      const image = new Image()
      image.src = icon
      image.onload = () => {
        // 根据需要调整图标的位置和大小
        const iconWidth = signWidth * 0.75 // 假设图标宽度为标牌宽度的 75%
        const iconHeight = signHeight * 0.75 // 假设图标高度为标牌高度的 75%
        ctx.drawImage(
          image,
          x - iconWidth / 2,
          y - iconHeight / 2,
          iconWidth,
          iconHeight
        )
      }
    }

    // 绘制指向线和箭头头
    this.drawArrow(ctx, x, y, arrowDirection, signWidth, signHeight, fontSize)
  }

  // 绘制带有自动判定方向的指向线的指示标牌，宽高自适应文本内容，并添加内边距
  public drawSignWithArrow2(
    sourcePointX: number,
    sourcePointY: number,
    text: string,
    fontSize: number,
    textColor: string,
    bgColor: string,
    position:
      | 'leftTop'
      | 'top'
      | 'rightTop'
      | 'rightBottom'
      | 'bottom'
      | 'leftBottom' = 'leftBottom',
    icon?: string
  ): void {
    const ctx = this.ctx
    if (!ctx) return

    this.drawPoint(sourcePointX, sourcePointY, 3, 'red')

    ctx.font = `${fontSize}px sans-serif`
    const textWidth = ctx.measureText(text).width
    const padding = 5
    const signWidth = textWidth + padding * 2
    const signHeight = fontSize + padding * 2

    let x = 0,
      y = 0,
      lineX = 0,
      lineY = 0

    // 根据位置确定标牌的坐标
    const paddingX2 = padding * 2
    switch (position) {
      case 'leftTop':
        x = sourcePointX - signWidth - paddingX2
        y = sourcePointY - signHeight - paddingX2
        lineX = x + signWidth / 1.2
        lineY = y + signHeight
        break
      case 'top':
        x = sourcePointX - signWidth / 2
        y = sourcePointY - signHeight - paddingX2
        lineX = x + signWidth / 2
        lineY = y + signHeight
        break
      case 'rightTop':
        x = sourcePointX + paddingX2
        y = sourcePointY - signHeight - paddingX2
        lineX = x + signWidth / 4
        lineY = y + signHeight
        break
      case 'rightBottom':
        x = sourcePointX + paddingX2
        y = sourcePointY + paddingX2
        lineX = x + signWidth / 4
        lineY = y
        break
      case 'bottom':
        x = sourcePointX - signWidth / 2
        y = sourcePointY + paddingX2
        lineX = x + signWidth / 2
        lineY = y
        break
      case 'leftBottom':
        x = sourcePointX - signWidth - paddingX2
        y = sourcePointY + paddingX2
        lineX = x + signWidth / 1.2
        lineY = y
        break
      default:
        x = sourcePointX - signWidth / 2
        y = sourcePointY - signHeight - paddingX2
        lineX = x + signWidth / 1.2
        lineY = y + signHeight
        break
    }

    // 绘制背景矩形（标牌）
    ctx.fillStyle = bgColor
    ctx.fillRect(x, y, signWidth, signHeight)

    // 绘制文本
    ctx.fillStyle = textColor
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(text, x + signWidth / 2, y + signHeight / 2)

    // 如果提供了图标路径，则绘制图标
    if (icon) {
      const image = new Image()
      image.src = icon
      image.onload = () => {
        const iconWidth = signWidth * 0.75
        const iconHeight = signHeight * 0.75
        ctx.drawImage(
          image,
          x + (signWidth - iconWidth) / 2,
          y + (signHeight - iconHeight) / 2,
          iconWidth,
          iconHeight
        )
      }
    }

    // 绘制指向线和箭头头
    this.drawPointerToSign(
      sourcePointX,
      sourcePointY,
      lineX,
      lineY,
      signWidth,
      signHeight
    )
  }

  // 绘制箭头的方法
  private drawArrow(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    direction: 'up' | 'down' | 'left' | 'right',
    signWidth: number,
    signHeight: number,
    fontSize: number
  ): void {
    ctx.strokeStyle = 'black' // 设置线条颜色
    ctx.lineWidth = 2 // 设置线条宽度
    ctx.beginPath()
    const arrowLength = fontSize * 1.5 // 箭头长度为字体大小的 1.5 倍
    const arrowWidth = fontSize * 0.5 // 箭头宽度为字体大小的 0.5 倍

    let startX: number, startY: number, endX: number, endY: number

    // 计算标牌中心点坐标
    const centerX = x - signWidth / 2
    const centerY = y - signHeight / 2

    switch (direction) {
      case 'up':
        startX = centerX
        startY = centerY - signHeight / 2 - arrowLength // 指向标牌的上边缘
        endX = startX - arrowWidth / 2
        endY = startY
        break
      case 'down':
        startX = centerX
        startY = centerY + signHeight / 2 + arrowLength // 指向标牌的下边缘
        endX = startX - arrowWidth / 2
        endY = startY
        break
      case 'left':
        startX = centerX - signWidth / 2 - arrowLength // 指向标牌的左边缘
        startY = centerY
        endX = startX
        endY = startY - arrowWidth / 2
        break
      case 'right':
        startX = centerX + signWidth / 2 + arrowLength // 指向标牌的右边缘
        startY = centerY
        endX = startX
        endY = startY - arrowWidth / 2
        break
    }

    ctx.moveTo(startX, startY)
    ctx.lineTo(centerX, centerY) // 指向标牌的中心
    ctx.lineTo(endX, endY)
    ctx.stroke()

    // 绘制箭头头
    this.drawArrowHead(ctx, endX, endY, direction, arrowWidth)
  }

  // 绘制箭头头的方法
  private drawArrowHead(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    direction: 'up' | 'down' | 'left' | 'right',
    arrowWidth: number
  ): void {
    ctx.beginPath()
    const arrowLength = arrowWidth * 1.5

    switch (direction) {
      case 'up':
        ctx.moveTo(x, y)
        ctx.lineTo(x - arrowLength / 2, y + arrowWidth / 2)
        ctx.lineTo(x + arrowLength / 2, y + arrowWidth / 2)
        break
      case 'down':
        ctx.moveTo(x, y)
        ctx.lineTo(x - arrowLength / 2, y - arrowWidth / 2)
        ctx.lineTo(x + arrowLength / 2, y - arrowWidth / 2)
        break
      case 'left':
        ctx.moveTo(x, y)
        ctx.lineTo(x + arrowWidth / 2, y - arrowLength / 2)
        ctx.lineTo(x + arrowWidth / 2, y + arrowLength / 2)
        break
      case 'right':
        ctx.moveTo(x, y)
        ctx.lineTo(x - arrowWidth / 2, y - arrowLength / 2)
        ctx.lineTo(x - arrowWidth / 2, y + arrowLength / 2)
        break
    }

    ctx.closePath()
    ctx.fill()
  }

  // 自动判定指向线方向的方法
  private autoDetectArrowDirection(
    x: number,
    y: number,
    size: number
  ): 'up' | 'down' | 'left' | 'right' {
    const canvasWidth = this.canvas.width
    const canvasHeight = this.canvas.height

    if (y - size < size) {
      // 标牌在 Canvas 顶部
      return 'down'
    } else if (x + size > canvasWidth) {
      // 标牌在 Canvas 右侧
      return 'left'
    } else if (x - size < 0) {
      // 标牌在 Canvas 左侧
      return 'right'
    } else if (y + size > canvasHeight) {
      // 标牌在 Canvas 底部
      return 'up'
    } else {
      // 默认方向，或者当标牌位置不是在边缘时
      return 'up'
    }
  }

  // 绘制指向标牌的指向线
  public drawPointerToSign(
    srcX: number,
    srcY: number,
    signX: number,
    signY: number,
    signWidth: number,
    signHeight: number,
    color = 'black',
    pointerLength = 10
  ): void {
    const ctx = this.ctx
    if (!ctx) return

    // 绘制指向线
    ctx.strokeStyle = color
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(srcX, srcY)
    ctx.lineTo(signX, signY)
    ctx.stroke()

    // 确定标牌的中心点
    const signCenterX = signX + signWidth / 2
    const signCenterY = signY + signHeight / 2

    // 计算指向线与标牌中心点的斜率
    const dx = signCenterX - srcX
    const dy = signCenterY - srcY

    // 计算单位向量
    const mag = Math.sqrt(dx * dx + dy * dy)
    const unitX = dx / mag
    const unitY = dy / mag

    // 绘制箭头头
    this.drawPointerArrowHead(
      ctx,
      signCenterX,
      signCenterY,
      unitX,
      unitY,
      pointerLength,
      color
    )
  }

  // 绘制箭头头的方法
  private drawPointerArrowHead(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    dx: number,
    dy: number,
    length: number,
    color: string
  ): void {
    ctx.fillStyle = color
    ctx.beginPath()
    ctx.moveTo(x, y)
    ctx.lineTo(x + dx * length, y + dy * length)
    ctx.lineTo(x - length * dx, y - length * dy)
    ctx.lineTo(x + length * dx, y + length * dy)
    ctx.lineTo(x, y)
    ctx.closePath()
    ctx.fill()
  }

  // 标牌数据，用于存储和检索标牌信息
  private signs: {
    id: string
    x: number
    y: number
    text: string
    textColor: string
    bgColor: string
    size: number
    icon?: string
  }[] = []

  // 添加标牌到 signs 数组中，用于后续编辑操作
  public addSign(sign: {
    id: string
    x: number
    y: number
    text: string
    textColor: string
    bgColor: string
    size: number
    icon?: string
  }): void {
    this.signs.push(sign)
  }

  // 移动标牌
  public moveSign(id: string, offsetX: number, offsetY: number): void {
    const sign = this.signs.find((sign) => sign.id === id)
    if (sign) {
      sign.x += offsetX
      sign.y += offsetY
      this.redrawSign(sign)
    }
  }

  // 缩放标牌
  public scaleSign(id: string, scale: number): void {
    const sign = this.signs.find((sign) => sign.id === id)
    if (sign) {
      sign.size *= scale
      this.redrawSign(sign)
    }
  }

  // 旋转标牌
  public rotateSign(id: string, angle: number): void {
    // 旋转标牌的实现较为复杂，需要考虑旋转中心点和Canvas的变换属性
    // 此处仅为示意，具体实现需要更多的数学计算
    const sign = this.signs.find((sign) => sign.id === id)
    if (sign) {
      // 此处应添加旋转标牌的逻辑
      console.log(angle)
    }
  }

  // 删除标牌
  public deleteSign(id: string): void {
    this.signs = this.signs.filter((sign) => sign.id !== id)
    this.clearCanvas()
    this.redrawAllSigns()
  }

  // 编辑标牌文本
  public editSignText(id: string, newText: string): void {
    const sign = this.signs.find((sign) => sign.id === id)
    if (sign) {
      sign.text = newText
      this.redrawSign(sign)
    }
  }

  // 更改标牌样式
  public changeSignStyle(id: string, textColor: string, bgColor: string): void {
    const sign = this.signs.find((sign) => sign.id === id)
    if (sign) {
      sign.textColor = textColor
      sign.bgColor = bgColor
      this.redrawSign(sign)
    }
  }

  // 重新绘制单个标牌
  private redrawSign(sign: {
    id: string
    x: number
    y: number
    text: string
    textColor: string
    bgColor: string
    size: number
    icon?: string
  }): void {
    this.ctx.clearRect(
      sign.x - sign.size * 2,
      sign.y - sign.size,
      sign.size * 4,
      sign.size * 2
    )
    this.drawSign(
      sign.x,
      sign.y,
      sign.text,
      sign.textColor,
      sign.bgColor,
      sign.size,
      sign.icon
    )
  }

  // 重新绘制所有标牌
  private redrawAllSigns(): void {
    this.signs.forEach((sign) => {
      this.redrawSign(sign)
    })
  }
}
